import * as Sentry from '@sentry/react';

/**
 * Download a (presigned, cross-origin) URL and save it under a forced
 * filename. The `download` attribute is ignored on cross-origin hrefs and
 * some S3-compatible backends (local Supabase storage) drop the
 * ResponseContentDisposition presign param, so the only reliable way to
 * control the save-as name is fetch → blob → same-origin object URL.
 * Falls back to opening the raw URL if the fetch fails (e.g. CORS —
 * the bucket must carry the app-origin CORS config; see
 * vNext-api/docs/s3-cors.md).
 */
export async function saveUrlAs(url: string, filename: string): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch download (${response.status})`);
    }
    const blob = await response.blob();
    saveBlobAs(blob, filename);
  } catch (err) {
    Sentry.captureException(err);
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function saveBlobAs(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Compose + save a CSV built client-side (e.g. the screening-batch
 * register). Quotes every field; prepends nothing (no BOM). */
export function downloadCsvFile(rows: string[][], filename: string): void {
  const escape = (value: string) => `"${value.replaceAll('"', '""')}"`;
  const body = rows.map((row) => row.map(escape).join(',')).join('\r\n');
  saveBlobAs(new Blob([body], { type: 'text/csv;charset=utf-8' }), filename);
}
